import { useCallback, useEffect, useMemo } from "react";
import { CompanyShippingAddress } from "@forgepoint/shared";
import { useCompanyShippingAddressesStore } from "@/modules/companyShippingAddresses/companyShippingAddressesStore";
import { EntitySelector } from "@/components/EntitySelector";

interface ShippingAddressSelectorProps {
  companyId: string | null;
  currentlySelectedId: string | null;
  onSelectId: (id: string | null) => void;
  disabled?: boolean;
  size?: "default" | "sm";
  className?: string;
}

/**
 * Selector component for choosing a shipping address from a company's saved addresses.
 * Wraps the generic EntitySelector with shipping-address-specific configuration.
 * Disabled when no company is selected.
 */
export function ShippingAddressSelector({
  companyId,
  currentlySelectedId,
  onSelectId,
  disabled = false,
  size = "default",
  className,
}: ShippingAddressSelectorProps) {
  const { shippingAddresses, listLoading, fetchByCompany } =
    useCompanyShippingAddressesStore();

  useEffect(() => {
    if (companyId) {
      fetchByCompany(companyId);
    }
  }, [companyId, fetchByCompany]);

  const filteredAddresses = useMemo(() => {
    if (!companyId) {
      return new Map<string, CompanyShippingAddress>();
    }
    const filtered = new Map<string, CompanyShippingAddress>();
    shippingAddresses.forEach((addr) => {
      if (addr.companyId === companyId) {
        filtered.set(addr.id, addr);
      }
    });
    return filtered;
  }, [shippingAddresses, companyId]);

  const fetchEntities = useCallback(async () => {
    if (!companyId) {
      return [];
    }
    return fetchByCompany(companyId);
  }, [companyId, fetchByCompany]);

  const getDisplayName = useCallback((addr: CompanyShippingAddress) => {
    return addr.label;
  }, []);

  const getSecondaryInfo = useCallback((addr: CompanyShippingAddress) => {
    const parts = [];
    if (addr.address.city && addr.address.state) {
      parts.push(`${addr.address.city}, ${addr.address.state}`);
    } else if (addr.address.city) {
      parts.push(addr.address.city);
    } else if (addr.address.state) {
      parts.push(addr.address.state);
    }
    if (addr.address.zipcode) {
      parts.push(addr.address.zipcode);
    }
    return parts.join(" ") || "";
  }, []);

  const renderExtraInfo = useCallback(
    (addr: CompanyShippingAddress) =>
      addr.address.address_line_1 ? (
        <div className="text-xs text-muted-foreground line-clamp-1">
          {addr.address.address_line_1}
        </div>
      ) : null,
    [],
  );

  return (
    <EntitySelector<CompanyShippingAddress>
      entities={filteredAddresses}
      listLoading={listLoading}
      fetchEntities={fetchEntities}
      getDisplayName={getDisplayName}
      getSecondaryInfo={getSecondaryInfo}
      renderExtraInfo={renderExtraInfo}
      currentlySelectedId={currentlySelectedId}
      onSelectId={onSelectId}
      placeholder={
        companyId ? "Select ship-to address" : "Select customer first"
      }
      searchPlaceholder="Search addresses..."
      className={className}
      disabled={disabled || !companyId}
      size={size}
      entityLabel="shipping addresses"
    />
  );
}
